import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Label,
} from "recharts";
import { Users, TrendingUp, Building2, Briefcase, Layers, Info } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SectorStat {
  sector: string;
  total: number;
}

interface JobTitleStat {
  jobTitle: string;
  total: number;
}

interface SectorJobStat {
  sector: string;
  jobTitle: string;
  total: number;
  vidas: number;
}

interface CampaignStatsChartsProps {
  bySector: SectorStat[];
  byJobTitle: JobTitleStat[];
  bySectorAndJob: SectorJobStat[];
  totalResponses: number;
  totalInProgress: number;
}

// Cores para os gráficos
const COLORS = [
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#ef4444", // red-500
  "#8b5cf6", // violet-500
  "#ec4899", // pink-500
  "#06b6d4", // cyan-500
  "#f97316", // orange-500
];

/**
 * InfoTooltip — ícone (i) com tooltip explicativo
 */
function InfoTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground cursor-help ml-1 inline-block" />
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function CampaignStatsCharts({
  bySector,
  byJobTitle,
  bySectorAndJob,
  totalResponses,
  totalInProgress,
}: CampaignStatsChartsProps) {
  const [selectedSector, setSelectedSector] = useState<string>("all");

  // Se não houver respostas, mostrar estado vazio
  if (totalResponses === 0 && totalInProgress === 0 && bySectorAndJob.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">
            Nenhuma resposta ainda
          </p>
        </CardContent>
      </Card>
    );
  }

  // Contadores
  const uniqueSectors = new Set(bySector.map((s) => s.sector)).size;
  const uniqueJobTitles = new Set(byJobTitle.map((j) => j.jobTitle)).size;

  // V81: Combinações Setor+Cargo — calculado a partir dos dados já disponíveis
  const combinationStats = useMemo(() => {
    try {
      if (!bySectorAndJob || bySectorAndJob.length === 0) return null;

      // Total de combinações na estrutura (escopo completo da campanha)
      const totalCombinations = bySectorAndJob.length;

      // Combinações que receberam pelo menos 1 resposta
      const respondedCombinations = bySectorAndJob.filter((item) => item.total > 0).length;

      return {
        responded: respondedCombinations,
        total: totalCombinations,
        percentage: totalCombinations > 0
          ? ((respondedCombinations / totalCombinations) * 100).toFixed(1)
          : "0",
      };
    } catch (e) {
      console.warn("[V81] Erro ao calcular combinações setor+cargo:", e);
      return null;
    }
  }, [bySectorAndJob]);

  // Preparar dados para "Respostas por Setor"
  const sectorData = bySector.map((item) => ({
    name: item.sector,
    value: item.total,
    percentage: ((item.total / totalResponses) * 100).toFixed(1),
  }));

  // Preparar dados para "Respostas por Cargo" (top 10 + outros)
  const sortedJobTitles = [...byJobTitle].sort((a, b) => b.total - a.total);
  const top10Jobs = sortedJobTitles.slice(0, 10);
  const othersTotal = sortedJobTitles
    .slice(10)
    .reduce((sum, item) => sum + item.total, 0);

  const jobTitleData = top10Jobs.map((item) => ({
    name: item.jobTitle,
    value: item.total,
  }));

  if (othersTotal > 0) {
    jobTitleData.push({ name: "Outros", value: othersTotal });
  }

  // V27: Preparar dados para "Setor x Cargo" (tabela) com ordenação estável
  // A ordenação vem do backend (ORDER BY sector ASC, jobTitle ASC)
  // Não reordenar no frontend para manter estabilidade
  const filteredSectorJobData =
    selectedSector === "all"
      ? bySectorAndJob
      : bySectorAndJob.filter((item) => item.sector === selectedSector);

  // V27: tableData com vidas e % participação (sem reordenar)
  const tableData = filteredSectorJobData.map((item) => {
    const vidas = item.vidas || 0;
    const participacao = vidas > 0 ? ((item.total / vidas) * 100) : 0;
    return {
      sector: item.sector,
      jobTitle: item.jobTitle,
      total: item.total,
      vidas,
      participacao: Math.min(participacao, 100), // Cap at 100%
      participacaoStr: vidas > 0 ? `${participacao.toFixed(1)}%` : "—",
    };
  });

  // Decidir tipo de gráfico para setor
  const usePieChart = bySector.length <= 6;

  return (
    <div className="space-y-6">
      {/* V81: Linha de Cards de Resumo — reordenados: Respostas | Combinações | Setores | Cargos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Card 1: Respostas Completas */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Respostas Completas
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalResponses}</div>
          </CardContent>
        </Card>

        {/* Card 2: Em Andamento */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Em Andamento
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalInProgress}</div>
          </CardContent>
        </Card>

        {/* V81: Card 3: Combinações Setor + Cargo (NOVO) */}
        {combinationStats && (
          <Card className="border-primary/20 bg-primary/[0.02]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center">
                Combinações Setor + Cargo
                <InfoTooltip text="Unidade analítica do sistema. Cada combinação setor+cargo gera uma análise independente. O mesmo cargo em setores diferentes é analisado separadamente." />
              </CardTitle>
              <Layers className="h-4 w-4 text-primary/60" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {combinationStats.responded}
                <span className="text-sm font-normal text-muted-foreground ml-1">
                  / {combinationStats.total}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {combinationStats.percentage}% com respostas
              </p>
            </CardContent>
          </Card>
        )}

        {/* Card 4: Setores Distintos (com tooltip V81) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              Setores
              <InfoTooltip text="Quantidade de setores distintos nas respostas enviadas. A análise do sistema considera a combinação Setor + Cargo." />
            </CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{uniqueSectors}</div>
          </CardContent>
        </Card>

        {/* Card 5: Cargos Distintos (com tooltip V81) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              Cargos
              <InfoTooltip text="Quantidade de cargos distintos nas respostas enviadas. Cargos iguais em setores diferentes são analisados separadamente." />
            </CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{uniqueJobTitles}</div>
          </CardContent>
        </Card>
      </div>

      {/* Respostas por Setor */}
      {bySector.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Respostas por Setor</CardTitle>
          </CardHeader>
          <CardContent>
            {usePieChart ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={sectorData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {sectorData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value: number) => [`${value} respostas`, "Total"]}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(300, sectorData.length * 50)}>
                <BarChart data={sectorData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} />
                  <RechartsTooltip
                    formatter={(value: number, name, props) => [
                      `${value} respostas (${props.payload.percentage}%)`,
                      "Total",
                    ]}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                    }}
                  />
                  <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                    {sectorData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Respostas por Cargo */}
      {byJobTitle.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Respostas por Cargo</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(300, jobTitleData.length * 50)}>
              <BarChart data={jobTitleData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} />
                <RechartsTooltip
                  formatter={(value: number) => [`${value} respostas`, "Total"]}
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                  }}
                />
                <Bar dataKey="value" radius={[0, 8, 8, 0]} label={{ position: "right" }}>
                  {jobTitleData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* V27: Setor x Cargo (Tabela) — ordenação estável, coluna Vidas, % Participação */}
      {bySectorAndJob.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Setor x Cargo</span>
              {bySector.length > 1 && (
                <Select value={selectedSector} onValueChange={setSelectedSector}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar por setor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os setores</SelectItem>
                    {bySector.map((s) => (
                      <SelectItem key={s.sector} value={s.sector}>
                        {s.sector}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Setor</th>
                    <th className="text-left py-2 px-2">Cargo</th>
                    <th className="text-center py-2 px-2">Respostas</th>
                    <th className="text-center py-2 px-2">Vidas</th>
                    <th className="text-left py-2 px-2 w-[200px]">% Participação</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, idx) => (
                    <tr key={`${item.sector}-${item.jobTitle}`} className="border-b hover:bg-muted/30">
                      <td className="py-2 px-2">{item.sector}</td>
                      <td className="py-2 px-2">{item.jobTitle}</td>
                      <td className="py-2 px-2 text-center font-mono">
                        {item.total}
                      </td>
                      <td className="py-2 px-2 text-center font-mono text-muted-foreground">
                        {item.vidas > 0 ? item.vidas : "—"}
                      </td>
                      <td className="py-2 px-2">
                        {item.vidas > 0 ? (
                          <div className="flex items-center gap-2">
                            <Progress
                              value={item.participacao}
                              className="h-2 w-[100px]"
                            />
                            <span className={`text-sm font-medium ${
                              item.participacao >= 100
                                ? "text-emerald-600 dark:text-emerald-400"
                                : item.participacao >= 50
                                ? "text-amber-600 dark:text-amber-400"
                                : "text-red-600 dark:text-red-400"
                            }`}>
                              {item.participacaoStr}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
